import React from 'react';
import { Select, Spin, Typography } from 'antd';
import type { ReminderTagOption } from '../services/ReminderService';
import { normalizeTurkish } from '../utils';

type TaggableMultiSelectProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  options?: ReminderTagOption[];
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  disabled?: boolean;
  onSearch?: (value: string) => void;
};

const normalizeTagText = (value: string): string => value.trim().replace(/\s+/g, ' ');

const dedupeTags = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const normalized = normalizeTagText(String(value || ''));
    if (!normalized) return;
    const key = normalized.toLocaleLowerCase('tr-TR');
    if (seen.has(key)) return;
    seen.add(key);
    result.push(normalized);
  });

  return result;
};

const TaggableMultiSelect: React.FC<TaggableMultiSelectProps> = ({
  value,
  onChange,
  options,
  loading = false,
  error = null,
  placeholder = 'Etiket seçiniz veya yazınız',
  disabled = false,
  onSearch,
}) => {
  const handleChange = (nextValues: string[]) => {
    onChange?.(dedupeTags(nextValues));
  };

  const optionFilter = (input: string, option?: { label?: string; value?: string }) =>
    normalizeTurkish(String(option?.label || option?.value || '')).includes(normalizeTurkish(input));

  const notFoundContent = loading ? (
    <div style={{ padding: 8, display: 'flex', justifyContent: 'center' }}>
      <Spin size='small' />
    </div>
  ) : error ? (
    <Typography.Text type='danger'>Etiketler yüklenemedi.</Typography.Text>
  ) : (
    <Typography.Text type='secondary'>Etiket bulunamadı.</Typography.Text>
  );

  return (
    <Select
      mode='tags'
      value={value}
      onChange={handleChange}
      showSearch
      onSearch={onSearch}
      allowClear
      disabled={disabled}
      placeholder={placeholder}
      tokenSeparators={[',']}
      optionFilterProp='label'
      filterOption={optionFilter}
      notFoundContent={notFoundContent}
    >
      {(options || []).map((option) => (
        <Select.Option key={option.id} value={option.name} label={option.name}>
          {option.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export default TaggableMultiSelect;
